import { FC, useState } from 'react'
import { 
  DefaultFormContainer,
  FormItemTitle,
  FormItemSubtitle,
  AddressOptions,
  FormInputErrorMessage,
  FlexDiv
} from './styles'
import StandardInput from '../../basic/StandardInput'
import StandardFormatInput from '../../basic/StandardFormatInput'
import { LeftBar } from '../../basic/LeftBar'
import OptionButton from '../../basic/OptionButton'
import { AddressData } from 'src/dtos/applicationData'
import { buildFormattedAddress } from 'src/utils/string'
import StandardSelect from 'src/components/basic/StandardSelect'
import { states } from 'src/constants/application'

interface AddressFormProps {
  title: string;
  setAddressData: (address: AddressData) => void;
  addressData?: AddressData;
  subtitle?: string;
  showOptions?: boolean;
  hasAddress?: boolean;
  setHasAddress?: (hasAddress: boolean) => void;
  isIncorrect?: boolean;
  errorCodes?: string[];
}

const AddressFormCustom: FC<AddressFormProps> = ({ 
  title, 
  subtitle, 
  showOptions,
  setAddressData,
  addressData,
  hasAddress,
  isIncorrect,
  setHasAddress,
  errorCodes = [],
}) => {
  const [address, setAddress] = useState(addressData)
  
  const handleInputChange = (type: string, value: string) => {
    setAddress(prev => {
      const exists = prev?.addressComponents?.findIndex(item => item?.types?.includes(type))
      if(exists !== undefined && exists >= 0) {
        const data = {
          ...prev,
          addressComponents: [
            ...prev?.addressComponents?.map((item, index) => index === exists ? {
              longName: value,
              shortName: value,
              types: [type]
            } : item) ?? []
          ],
        }

        setAddressData({...data, formattedAddress: buildFormattedAddress(data)})
        return {...data, formattedAddress: buildFormattedAddress(data)}
      } else {
        const data = {
          ...prev,
          addressComponents: [
            ...prev?.addressComponents ?? [],
            {
              longName: value,
              shortName: value,
              types: [type]
            }
          ]
        }
        setAddressData({...data, formattedAddress: buildFormattedAddress(data)})
        return {...data, formattedAddress: buildFormattedAddress(data)}
      }
    })
  }

  const handleOptionButton = (option: boolean) => {
    
    if(!!setHasAddress) {
      setHasAddress(option)
      if(!option) {
        setAddressData({})
        setAddress({})
      }
    }
  }
  
  return (
    <DefaultFormContainer>
      <FormItemTitle>{title}</FormItemTitle>
      {subtitle ? <FormItemSubtitle>{subtitle}</FormItemSubtitle> : null}
      {showOptions ? (
        <>
          <AddressOptions>
            <OptionButton
              checked={hasAddress === true}
              onChange={() => handleOptionButton(true)}
              isIncorrect={isIncorrect && hasAddress === undefined}
              errorCode='EC183'
            >
              Yes
            </OptionButton>
            <OptionButton
              checked={hasAddress === false}
              onChange={() => handleOptionButton(false)}
              isIncorrect={isIncorrect && hasAddress === undefined}
              errorCode='EC183'
            >
              No
            </OptionButton>
          </AddressOptions>
          {isIncorrect === true && showOptions === true && hasAddress === undefined ? (
            <FormInputErrorMessage>
              This is a required field
            </FormInputErrorMessage>
          ) : null}
        </>
      ) : null}
      {(!showOptions || (showOptions === true && hasAddress === true)) ? (
        <FlexDiv gap="24px" margin="24px 0 0 0">
          <LeftBar />
          <FlexDiv align='flex-start' gap="144px">
            <FlexDiv dir="column" gap="24px">
              <FlexDiv gap="24px">
                {/* <FlexDiv maxWidth='120px'>
                  <StandardInput
                    type="number"
                    label="Street Number"
                    value={address?.addressComponents?.find(item => item?.types?.includes('street_number'))?.shortName ?? ''}
                    onChange={e => handleInputChange('street_number', e.target.value)}
                    errorCode={errorCodes[0]}
                  />
                </FlexDiv> */}
                <StandardInput
                  label="Street Address Line 1"
                  value={address?.addressComponents?.find(item => item?.types?.includes('route'))?.shortName ?? ''}
                  onChange={e => handleInputChange('route', e.target.value)}
                  isIncorrect={isIncorrect && !address?.addressComponents?.find(item => item?.types?.includes('route'))?.shortName}
                  errorMessage={isIncorrect && !address?.addressComponents?.find(item => item?.types?.includes('route'))?.shortName ? 'This is a required field' : ''}
                  errorCode = {errorCodes[1]}
                />
              </FlexDiv>
              <StandardInput
                label="Street Address Line 2"
                value={address?.addressComponents?.find(item => item?.types?.includes('subpremise'))?.shortName ?? ''}
                onChange={e => handleInputChange('subpremise', e.target.value)}
              />
              <StandardInput
                label="Suite/Unit #"
                name="suiteNumber"
                onChange={e => {
                  setAddressData({ suiteNumber: e.target.value })
                  setAddress(prev => ({...prev, suiteNumber: e.target.value}))
                }}
                value={address?.suiteNumber ?? ''}
              />
              <FlexDiv gap="24px">
                <StandardInput
                  label="City"
                  name="city"
                  value={address?.addressComponents?.find(item => item?.types?.includes('locality'))?.shortName ?? ''}
                  onChange={e => handleInputChange('locality', e.target.value)}
                  isIncorrect={isIncorrect && !address?.addressComponents?.find(item => item?.types?.includes('locality'))?.shortName}
                  errorMessage={isIncorrect && !address?.addressComponents?.find(item => item?.types?.includes('locality'))?.shortName ? 'This is a required field' : ''}
                  errorCode = {errorCodes[2]}
                />
                <FlexDiv maxWidth='120px'>
                  <StandardSelect
                    label='State'
                    name="state"
                    value={address?.addressComponents?.find(item => item?.types?.includes('state'))?.shortName ?? ''}
                    onChange={e => handleInputChange('state', e.target.value)}
                    isIncorrect={isIncorrect && !address?.addressComponents?.find(item => item?.types?.includes('state'))?.shortName}
                    errorMessage={isIncorrect && !address?.addressComponents?.find(item => item?.types?.includes('state'))?.shortName ? 'This is a required field' : ''}
                    errorCode = {errorCodes[3]}
                     >
                    <option disabled value="">Select</option>
                    {states.map((position, index) => (
                      <option key={index} value={position.abbreviation}>{position.name}</option>
                  ))}
                </StandardSelect>
                </FlexDiv>
                <StandardFormatInput
                  label="Postal Code"
                  maskStyle='postalCode'
                  value={address?.addressComponents?.find(item => item?.types?.includes('postal_code'))?.shortName ?? ''}
                  onValueChange={({ value }) => handleInputChange('postal_code', value)}
                  isIncorrect={isIncorrect && !address?.addressComponents?.find(item => item?.types?.includes('postal_code'))?.shortName}
                  errorMessage={isIncorrect && !address?.addressComponents?.find(item => item?.types?.includes('postal_code'))?.shortName ? 'This is a required field' : ''}
                  errorCode = {errorCodes[4]}
                  
                />
              </FlexDiv>
            </FlexDiv>
          </FlexDiv>
        </FlexDiv>
      ) : null}
    </DefaultFormContainer>
  )
}

export default AddressFormCustom